/*
 * Quiz kit
 * <quiz-kit>: three-screen state machine (intro → questions → result) for the in-page section.
 * Lazy-init class (Motion.inView in the constructor, `initialized` latch + re-arm in connectedCallback) — see ARCHITECTURE.md.
 * The matcher (theme.quizKit) is a pure module, also exported for the Node unit test in the scratchpad.
 */
(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    window.theme = window.theme || {};
    window.theme.quizKit = api;
  }
})(function () {
  'use strict';

  const trim = (value) => String(value ?? '').trim();
  const norm = (value) => trim(value).toLowerCase();
  const list = (value) => trim(value).split(',').map(trim).filter(Boolean);
  const slug = (value) => norm(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
  const numberOf = (value) => {
    const match = String(value ?? '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  };

  /** "goal:strength, place:small|place:outdoor" → [["goal:strength"], ["place:small", "place:outdoor"]] */
  function parseGroups(tags) {
    return list(tags)
      .map((group) => group.split('|').map(norm).filter(Boolean))
      .filter((group) => group.length);
  }

  /** "16 kg, 3 kg" or "cast-kettlebell=16 kg; core-dumbbells=3 kg" → { any: [...], byHandle: {...} } */
  function parseVariantSpec(spec) {
    const out = { any: [], byHandle: {} };
    String(spec ?? '').split(/[;,]/).map(trim).filter(Boolean).forEach((part) => {
      const eq = part.indexOf('=');
      if (eq > 0) out.byHandle[norm(part.slice(0, eq))] = trim(part.slice(eq + 1));
      else out.any.push(part);
    });
    return out;
  }

  function normalize(raw) {
    const data = { ...raw };
    data.scale = Array.isArray(data.scale) ? data.scale.map(trim).filter(Boolean) : list(data.scale);
    data.auto = data.auto !== false; // auto-advance is the only mode exposed to merchants (manual Next only after an arrow-key pick)
    data.strings = data.strings || {};
    data.questions = (data.questions || []).map((question) => ({
      ...question,
      answers: (question.answers || []).map((answer) => ({
        ...answer,
        n: Number(answer.n),
        points: Number(answer.points) || 0,
        tagList: list(answer.tags).map(norm),
        variantSpec: parseVariantSpec(answer.variant)
      }))
    }));
    data.results = (data.results || []).map((result, index) => {
      const match = result.match || 'tags';
      return {
        ...result,
        order: index,
        match,
        groups: match === 'tags' ? parseGroups(result.tags) : [],
        min: result.min == null ? null : Number(result.min),
        max: result.max == null ? null : Number(result.max),
        products: (result.products || []).map(Number)
      };
    });
    data.products = data.products || {};
    return data;
  }

  function chosen(question, answers) {
    const n = Number(answers[question.key]);
    return n ? question.answers.find((answer) => answer.n === n) || null : null;
  }

  function tokensOf(data, answers) {
    const tokens = new Set();
    data.questions.forEach((question) => {
      const answer = chosen(question, answers);
      if (!answer) return;
      answer.tagList.forEach((tag) => tokens.add(tag));
      tokens.add(`${question.key}:${answer.n}`);
      const labelSlug = slug(answer.label);
      if (labelSlug) tokens.add(`${question.key}:${labelSlug}`);
    });
    return tokens;
  }

  function scoreOf(data, answers) {
    return data.questions.reduce((sum, question) => sum + (chosen(question, answers)?.points || 0), 0);
  }

  function passes(result, tokens, score) {
    if (result.match === 'always') return true;
    if (result.match === 'score') {
      return (result.min == null || score >= result.min) && (result.max == null || score <= result.max);
    }
    if (!result.groups.length) return false;
    return result.groups.every((group) => group.some((token) => {
      if (token.endsWith(':*')) {
        const prefix = token.slice(0, -1);
        for (const candidate of tokens) if (candidate.startsWith(prefix)) return true;
        return false;
      }
      return tokens.has(token);
    }));
  }

  function specificity(result) {
    if (result.match === 'tags') return result.groups.length;
    return result.match === 'score' ? 1 : 0;
  }

  /** Most specific passing result wins; ties go to block order (strict > keeps the first). */
  function pickResult(data, answers) {
    const tokens = tokensOf(data, answers);
    const score = scoreOf(data, answers);
    let best = null;
    data.results.forEach((result) => {
      if (!passes(result, tokens, score)) return;
      if (!best || specificity(result) > specificity(best)) best = result;
    });
    return { result: best, tokens, score };
  }

  function defaultVariantOf(product) {
    const found = (product.variants || []).find((variant) => variant.id === product.defaultVariant);
    return found || {
      id: product.defaultVariant,
      available: product.defaultAvailable !== false,
      price: product.price,
      options: [],
      image: product.image,
      quantityMin: product.quantityMin || 1
    };
  }

  function variantsWithValue(product, value, optionIndex) {
    const wanted = norm(value);
    return (product.variants || []).filter((variant) => (
      optionIndex >= 0 ? norm(variant.options[optionIndex]) === wanted : variant.options.some((option) => norm(option) === wanted)
    ));
  }

  /** Among candidates, prefer the ones sharing the most other option values with the default variant (Size × Colour tie-break). */
  function keepOthers(candidates, product) {
    const base = (product.variants || []).find((variant) => variant.id === product.defaultVariant);
    if (!base || candidates.length < 2) return candidates;
    const scored = candidates.map((variant) => ({
      variant,
      shared: variant.options.filter((option, index) => option === base.options[index]).length
    }));
    const max = Math.max(...scored.map((entry) => entry.shared));
    return scored.filter((entry) => entry.shared === max).map((entry) => entry.variant);
  }

  const firstAvailable = (variants) => variants.find((variant) => variant.available) || variants[0] || null;

  /** Answer "Variant values" → candidate variants of this product: per-handle value first, then any value, then the nearest number. */
  function candidatesByAnswers(data, product, answers) {
    let wanted = null;
    const handle = norm(product.handle);
    const anyList = [];
    data.questions.forEach((question) => {
      const answer = chosen(question, answers);
      if (!answer) return;
      if (answer.variantSpec.byHandle[handle]) wanted = answer.variantSpec.byHandle[handle];
      anyList.push(...answer.variantSpec.any);
    });
    if (!wanted && !anyList.length) return null;
    let candidates = wanted ? variantsWithValue(product, wanted, -1) : [];
    if (!candidates.length) {
      for (const candidate of anyList) {
        candidates = variantsWithValue(product, candidate, -1);
        if (candidates.length) { wanted = candidate; break; }
      }
    }
    if (!candidates.length) {
      // Numeric fallback: "20 kg" → the option value with the nearest number.
      const targets = [wanted, ...anyList].map(numberOf).filter((n) => n !== null);
      if (targets.length) {
        let bestValue = null;
        let bestDistance = Infinity;
        product.variants.forEach((variant) => variant.options.forEach((option) => {
          const n = numberOf(option);
          if (n === null) return;
          const distance = Math.min(...targets.map((target) => Math.abs(target - n)));
          if (distance < bestDistance) { bestDistance = distance; bestValue = option; }
        }));
        if (bestValue !== null) { candidates = variantsWithValue(product, bestValue, -1); wanted = bestValue; }
      }
    }
    return candidates.length ? { candidates, wanted } : null;
  }

  /** Size scale → this product's variants grouped by scale index, read from the option whose values overlap the scale most. */
  function sizesOnScale(data, product) {
    const scale = data.scale || [];
    if (!scale.length) return null;
    const scaleNorm = scale.map(norm);
    let optionIndex = -1;
    let bestHits = 0;
    (product.options || []).forEach((name, i) => {
      const hits = new Set();
      product.variants.forEach((variant) => {
        const value = variant.options[i];
        if (value != null && scaleNorm.includes(norm(value))) hits.add(norm(value));
      });
      if (hits.size > bestHits) { bestHits = hits.size; optionIndex = i; }
    });
    if (optionIndex < 0) return null;
    const present = new Map();
    product.variants.forEach((variant) => {
      const i = scaleNorm.indexOf(norm(variant.options[optionIndex]));
      if (i < 0) return;
      if (!present.has(i)) present.set(i, []);
      present.get(i).push(variant);
    });
    return present.size ? { present, scale } : null;
  }

  /** Nearest size that exists for the product (available sizes win ties, then the smaller one). */
  function nearestSize(present, index) {
    let bestIndex = -1;
    let bestKey = Infinity;
    for (const [i, variants] of present) {
      const key = Math.abs(i - index) * 2 + (variants.some((variant) => variant.available) ? 0 : 1);
      if (key < bestKey || (key === bestKey && i < bestIndex)) { bestKey = key; bestIndex = i; }
    }
    return bestIndex;
  }

  /**
   * Which variant of a product goes into the kit — no mode setting: answer "Variant values" pin a value (colour,
   * finish, weight), the section's size scale pins the size the score points to, and when both apply the variant
   * satisfying both wins (the size wins when none does). Neither → the default variant.
   */
  function variantFor(data, product, answers, score) {
    const none = { variant: defaultVariantOf(product), value: null, wanted: null };
    if (!product.variants || !product.variants.length) return none;
    const byAnswers = candidatesByAnswers(data, product, answers);
    const sized = sizesOnScale(data, product);
    if (sized) {
      const index = clamp(score, 0, sized.scale.length - 1);
      let present = sized.present;
      if (byAnswers) {
        const ids = new Set(byAnswers.candidates.map((variant) => variant.id));
        const both = new Map();
        for (const [i, variants] of present) {
          const hit = variants.filter((variant) => ids.has(variant.id));
          if (hit.length) both.set(i, hit);
        }
        if (both.size) present = both;
      }
      const bestIndex = nearestSize(present, index);
      const variant = firstAvailable(keepOthers(present.get(bestIndex), product));
      return { variant, value: sized.scale[bestIndex], wanted: sized.scale[index] };
    }
    if (!byAnswers) return none;
    const variant = firstAvailable(keepOthers(byAnswers.candidates, product));
    const value = variant ? (variant.options.find((option) => norm(option) === norm(byAnswers.wanted)) ?? byAnswers.wanted) : null;
    return { variant, value, wanted: byAnswers.wanted };
  }

  /** Replaces {token} / {token|fallback}; unknown tokens → '' on the storefront, a visible marker in design mode. */
  /** The rich text editor stores typed braces as entities (&lcub; / &rcub;) — normalise before matching tokens. */
  function decodeBraces(text) {
    return String(text ?? '')
      .replace(/&(?:lcub|lbrace|#123|#x7b);/gi, '{')
      .replace(/&(?:rcub|rbrace|#125|#x7d);/gi, '}');
  }

  function fill(text, vars, designMode, escape) {
    const safe = escape || ((value) => value);
    return decodeBraces(text).replace(/\{([\w:.-]+)(?:\|([^}]*))?\}/g, (match, key, fallback) => {
      const value = vars[key];
      if (value != null && value !== '') return safe(String(value));
      if (fallback != null) return safe(fallback);
      if (!designMode) return '';
      // Editor preview: a known token with no value yet is shown as-is; the warning marks a key no answer provides.
      const known = key in vars || /^variant:\d+$/.test(key);
      return known ? `{${key}}` : `⚠ {${key}}`;
    });
  }

  function chipsOf(data, answers) {
    return data.questions.map((question) => {
      const answer = chosen(question, answers);
      return answer ? { key: question.key, label: answer.chip || answer.label, question: question.title } : null;
    }).filter(Boolean);
  }

  function match(data, answers) {
    const { result, tokens, score } = pickResult(data, answers);
    const vars = { score: String(score), variant: '' };
    data.questions.forEach((question) => {
      // Every question key is a known token: the editor shows it plainly before it has a value (⚠ is for unknown keys).
      vars[`answer:${question.key}`] = '';
      vars[`phrase:${question.key}`] = '';
      const answer = chosen(question, answers);
      if (!answer) return;
      vars[`answer:${question.key}`] = answer.chip || answer.label;
      vars[`phrase:${question.key}`] = answer.phrase || '';
    });
    const chips = chipsOf(data, answers);
    if (!result) return { result: null, items: [], vars, score, tokens: Array.from(tokens), chips, addAllLabel: '' };

    const items = result.products
      .map((productId) => data.products[String(productId)])
      .filter(Boolean)
      .map((product) => {
        const picked = variantFor(data, product, answers, score);
        const variant = picked.variant;
        return {
          productId: product.id,
          handle: product.handle,
          title: product.title,
          url: product.url,
          variantId: variant ? variant.id : product.defaultVariant,
          value: picked.value,
          wanted: picked.wanted,
          options: variant ? variant.options : [],
          price: variant ? variant.price : product.price,
          available: variant ? variant.available !== false : product.defaultAvailable !== false,
          image: (variant && variant.image) || product.image,
          quantity: (variant && variant.quantityMin) || product.quantityMin || 1
        };
      });

    let scaleValue = null;
    if (data.scale.length) {
      scaleValue = data.scale[clamp(score, 0, data.scale.length - 1)];
    }
    vars.variant = scaleValue ?? (items.find((item) => item.value) || {}).value ?? '';
    items.forEach((item, index) => { vars[`variant:${index + 1}`] = item.value ?? ''; });

    const addAllRaw = data.strings.addAll || 'Add all';
    return { result, items, vars, score, tokens: Array.from(tokens), chips, addAllLabel: fill(addAllRaw, vars, false) };
  }

  /** Every answer combination (cap guards 8 × 4 = 65 536). */
  function* combos(data, cap = 5000) {
    const questions = data.questions;
    if (!questions.length) return;
    const counts = questions.map((question) => question.answers.length);
    const total = counts.reduce((product, count) => product * count, 1);
    const limit = Math.min(total, cap);
    for (let i = 0; i < limit; i++) {
      const answers = {};
      let rest = i;
      questions.forEach((question, index) => {
        answers[question.key] = (rest % counts[index]) + 1;
        rest = Math.floor(rest / counts[index]);
      });
      yield answers;
    }
  }

  /**
   * Design-mode checks — only the three silent failures the editor cannot catch itself:
   * a rule token no answer provides (typo), two questions sharing a key, and answer sets that end without a result.
   * Missing products / unreachable results are not errors (placeholders cover the former).
   */
  function audit(data, cap = 5000) {
    const notices = [];
    const keys = new Set();
    data.questions.forEach((question) => {
      if (keys.has(question.key)) notices.push({ code: 'duplicate_key', key: question.key });
      keys.add(question.key);
    });
    const known = new Set();
    data.questions.forEach((question) => question.answers.forEach((answer) => {
      answer.tagList.forEach((tag) => known.add(tag));
      known.add(`${question.key}:${answer.n}`);
      const labelSlug = slug(answer.label);
      if (labelSlug) known.add(`${question.key}:${labelSlug}`);
    }));
    const reported = new Set();
    data.results.forEach((result, index) => {
      if (result.match !== 'tags') return;
      result.groups.flat().forEach((token) => {
        const unknown = token.endsWith(':*')
          ? !data.questions.some((question) => `${question.key}:` === token.slice(0, -1))
          : !known.has(token);
        const key = `${index}:${token}`;
        if (unknown && !reported.has(key)) { reported.add(key); notices.push({ code: 'unknown_token', n: index + 1, token }); }
      });
    });
    if (data.questions.length && data.results.length) {
      const total = data.questions.reduce((product, question) => product * question.answers.length, 1);
      if (total <= cap) {
        let unmatched = 0;
        for (const answers of combos(data, cap)) if (!pickResult(data, answers).result) unmatched++;
        if (unmatched) notices.push({ code: 'unmatched', count: unmatched });
      }
      else if (!data.results.some((result) => result.match === 'always')) {
        notices.push({ code: 'no_fallback' });
      }
    }
    return notices;
  }

  return { normalize, match, fill, combos, audit, parseGroups, parseVariantSpec, list, slug };
});

if (typeof window !== 'undefined' && window.customElements && !customElements.get('quiz-kit')) {
  const quizKit = window.theme.quizKit;
  const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const strings = () => ({
    questionOf: 'Question [n] of [total]',
    of: '[n] / [total]',
    done: 'Done',
    pickOneAuto: 'Pick one — we move on automatically.',
    pickOneManual: 'Pick one, then press Next.',
    added: 'Added to cart',
    addedOf: 'Added [count] of [total] to cart',
    addAvailable: 'Add available ([count])',
    previewResult: 'Preview — result [n] of [total]',
    noResultTitle: 'No match yet',
    cartError: 'Something went wrong. Please try again.',
    notices: {},
    ...(window.theme.quizStrings || {})
  });

  const format = (template, params = {}) => Object.keys(params).reduce((text, key) => text.split(`[${key}]`).join(params[key]), String(template ?? ''));

  const money = (cents) => theme.Currency.formatMoney(cents, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);

  const stickyHeaderHeight = () => {
    const root = getComputedStyle(document.documentElement);
    const value = parseFloat(root.getPropertyValue('--sticky-header-height')) || parseFloat(root.getPropertyValue('--header-height')) || 0;
    if (value) return value;
    // Before theme.js has measured the header (early restore after the customer-form redirect).
    return document.querySelector('.header-sticky')?.getBoundingClientRect().height || 0;
  };

  class QuizKit extends BaseElement {
    constructor() {
      super();

      Motion.inView(this, this.init.bind(this), { margin: '400px 0px 400px 0px' });
    }

    connectedCallback() {
      super.connectedCallback();

      this.strings = strings();

      if (window.Shopify?.designMode) {
        const section = this.closest('.shopify-section');
        this.on(this, 'shopify:block:select', this.onBlockSelect.bind(this));
        this.on(this, 'shopify:block:deselect', this.onBlockDeselect.bind(this));
        if (section) {
          this.on(section, 'shopify:section:select', (event) => {
            if (!this.initialized || event.detail?.load) return;
            this.render();
          });
        }
      }

      // Re-arm after a DOM move (theme editor reorder) or a section re-render.
      if (this.initialized) {
        this.initialized = false;
        this.init();
      }
      else if (this.hasPendingRestore()) {
        // Coming back from the customer form: build the result right away, before Motion.inView fires.
        this.init();
      }
    }

    get done() {
      return this.state.current >= this.data.questions.length;
    }

    get storageKey() {
      return `${theme.settings.themeName}:${window.Shopify?.theme?.id ?? ''}:quiz-kit:${this.dataset.sectionId}`;
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      const json = this.querySelector('[data-quiz-data]');
      if (!json) return;
      this.data ??= quizKit.normalize(JSON.parse(json.textContent));
      this.state ??= this.restoreDesignState() ?? {
        screen: 'intro',
        current: 0,
        answers: {},
        email: null,
        result: null,
        preview: null,
        busy: false
      };

      this.cacheRefs();

      this.on(this, 'change', this.onChange.bind(this));
      this.on(this, 'click', this.onClick.bind(this));
      this.on(this, 'keydown', this.onKeydown.bind(this));
      this.on(this, 'pointerdown', () => { this.arrowPick = false; }, { passive: true });
      this.on(this, 'submit', this.onSubmit.bind(this));
      // Result cards are product-card-bundle cards: a variant picked in a card replaces the pinned one for "Add all".
      if (theme.pubsub?.subscribe) {
        this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, this.onCardVariantChange.bind(this)));
      }
      this.registerCleanup(() => {
        clearTimeout(this.advanceTimer);
        this.cardsAbort?.abort();
      });

      // Desktop: the email block lives in the side panel (demo layout); mobile: under the result actions.
      this.emailBaseTags ??= this.refs.emailTags?.value ?? '';
      if (this.refs.email && this.refs.emailSlot) {
        this.emailHome ??= { parent: this.refs.email.parentNode, next: this.refs.email.nextSibling };
        this.desktopQuery ??= window.matchMedia('(min-width: 1024px)');
        this.on(this.desktopQuery, 'change', () => this.placeEmail());
      }

      this.restore();
      this.render();
      if (this.done && !this.restoring) {
        // Re-init after a DOM move / editor re-render on the result screen: rebuild the cards too.
        if (!this.state.result) this.resolve({ track: false });
        this.showCards();
      }
      if (window.Shopify?.designMode) this.audit();
    }

    cacheRefs() {
      const q = (selector, root = this) => root.querySelector(selector);
      const all = (selector, root = this) => Array.from(root.querySelectorAll(selector));
      this.refs = {
        intro: q('[data-quiz-screen="intro"]'),
        start: q('[data-quiz-start]'),
        run: q('[data-quiz-screen="run"]'),
        ask: q('[data-quiz-ask]'),
        fieldsets: all('[data-quiz-q]'),
        back: q('[data-quiz-back]'),
        next: q('[data-quiz-next]'),
        hint: q('[data-quiz-hint]'),
        result: q('[data-quiz-result]'),
        eyebrow: q('[data-quiz-eyebrow]'),
        title: q('[data-quiz-title]'),
        text: q('[data-quiz-text]'),
        preview: q('[data-quiz-preview]'),
        cards: q('[data-quiz-cards]'),
        noResult: q('[data-quiz-noresult]'),
        placeholders: q('[data-quiz-placeholders]'),
        placeholderNote: q('[data-quiz-placeholder-note]'),
        actions: all('[data-quiz-actions]'),
        addButtons: all('[data-quiz-add]'),
        addLabels: all('[data-quiz-add-label]'),
        error: q('[data-quiz-error]'),
        email: q('[data-quiz-email]'),
        emailForm: q('[data-quiz-email-form]'),
        emailTags: q('[data-quiz-email-tags]'),
        emailNews: q('[data-quiz-email-news]'),
        emailSuccess: q('[data-quiz-email-success]'),
        emailSkip: q('[data-quiz-email-skip]'),
        emailSlot: q('[data-quiz-email-slot]'),
        live: q('[data-quiz-live]'),
        steps: all('[data-quiz-steps]'),
        notice: q('[data-quiz-notice]')
      };
    }

    /* ---------- state transitions ---------- */

    start() {
      this.state.screen = 'run';
      this.go(this.nextIndex(), { focus: true });
      this.track('start');
    }

    pick(key, n, { viaArrow = false } = {}) {
      const wasAnswered = this.state.answers[key] != null;
      this.state.answers[key] = Number(n);
      this.state.result = null;
      this.state.preview = null;
      clearTimeout(this.advanceTimer);
      this.track('answer', { key, n: Number(n) });

      const current = this.data.questions[this.state.current];
      const autoAdvance = this.data.auto && !viaArrow && current && current.key === key;
      if (autoAdvance) {
        this.render();
        const delay = theme.config.motionReduced ? 0 : 280;
        this.advanceTimer = setTimeout(() => this.go(this.nextIndex(), { focus: true }), delay);
      }
      else {
        if (!wasAnswered || !this.done) this.render();
        else this.go(this.data.questions.length, { focus: false });
      }
    }

    nextIndex() {
      const index = this.data.questions.findIndex((question) => this.state.answers[question.key] == null);
      return index === -1 ? this.data.questions.length : index;
    }

    /** Previous question. The intro is one-way: once started there is no way back to it (Back is hidden on question 1). */
    back() {
      if (this.state.current <= 0) return;
      this.go(this.state.current - 1, { focus: true });
    }

    next() {
      const current = this.data.questions[this.state.current];
      if (!current || this.state.answers[current.key] == null) return;
      this.go(this.nextIndex() > this.state.current ? this.nextIndex() : this.state.current + 1, { focus: true });
    }

    change(key) {
      const index = this.data.questions.findIndex((question) => question.key === key);
      if (index === -1) return;
      this.state.screen = 'run';
      this.go(index, { focus: true });
    }

    restart() {
      clearTimeout(this.advanceTimer);
      this.state.answers = {};
      this.state.result = null;
      this.state.preview = null;
      this.state.email = null;
      this.clearStorage();
      this.state.screen = 'run';
      this.go(0, { focus: true });
      this.track('restart');
    }

    go(index, { focus = false, quiet = false, track = true } = {}) {
      clearTimeout(this.advanceTimer);
      this.arrowPick = false;
      this.state.current = clamp(index, 0, this.data.questions.length);
      this.state.screen = 'run';
      if (this.done) {
        const fresh = !this.state.result;
        this.resolve({ track: track && fresh });
        this.showCards();
      }
      this.render();
      if (quiet) return;
      this.reveal();
      this.announce();
      if (focus) this.focusCurrent();
    }

    resolve({ track = true } = {}) {
      const answers = this.state.answers;
      let output = quizKit.match(this.data, answers);
      if (this.state.preview) {
        const forced = this.data.results.find((result) => result.id === this.state.preview);
        if (forced) {
          const scoped = { ...this.data, results: [forced] };
          output = quizKit.match(scoped, answers);
          output.result = forced;
        }
      }
      this.state.result = output;
      if (track) this.track('result', { result: output.result ? output.result.id : null, title: output.result ? output.result.title : null, answers: { ...answers } });
      return output;
    }

    /* ---------- rendering ---------- */

    render() {
      const { refs, state, data } = this;
      if (!refs) return;
      const total = data.questions.length;
      const answered = data.questions.filter((question) => state.answers[question.key] != null).length;
      const done = this.done && state.screen === 'run';
      const current = state.screen === 'run' && !done ? data.questions[state.current] : null;

      refs.intro && (refs.intro.hidden = state.screen !== 'intro');
      refs.run && (refs.run.hidden = state.screen !== 'run');

      refs.fieldsets.forEach((fieldset, index) => {
        const question = data.questions[index];
        fieldset.hidden = !current || index !== state.current;
        if (!question) return;
        const picked = state.answers[question.key];
        fieldset.querySelectorAll('[data-quiz-answer]').forEach((input) => { input.checked = picked != null && Number(input.value) === Number(picked); });
      });
      if (refs.ask) refs.ask.hidden = !current;

      const currentAnswered = current ? state.answers[current.key] != null : false;
      const showNext = !!current && currentAnswered && (!data.auto || this.arrowPick === true || this.manualNext === true);
      if (refs.back) refs.back.hidden = state.current <= 0;
      if (refs.next) refs.next.hidden = !showNext;
      let hint = '';
      if (current) {
        hint = data.auto ? this.strings.pickOneAuto : this.strings.pickOneManual;
      }
      if (refs.hint) refs.hint.textContent = hint;

      // progress + steps (every copy: desktop panel and mobile bar)
      const progress = total ? Math.round((answered / total) * 100) : 0;
      this.style.setProperty('--quiz-progress', `${progress}%`);
      const chips = quizKit.match(data, state.answers).chips;
      refs.steps.forEach((steps) => {
        // Header: "Progress n / total" while answering; the full panel switches to "Your answers · Tap to change" once done.
        const compact = steps.classList.contains('quiz-kit__steps--compact');
        const count = steps.querySelector('[data-quiz-count]');
        if (count) {
          count.textContent = done ? this.strings.done : format(this.strings.of, { n: Math.min(answered + (current ? 1 : 0), total), total });
          count.hidden = done && !compact;
        }
        const labelProgress = steps.querySelector('[data-quiz-label-progress]');
        const labelAnswers = steps.querySelector('[data-quiz-label-answers]');
        if (labelProgress) labelProgress.hidden = done;
        if (labelAnswers) labelAnswers.hidden = !done;
        const changeHint = steps.querySelector('[data-quiz-change-hint]');
        if (changeHint) changeHint.hidden = !done;
        steps.querySelectorAll('[data-quiz-bar] > i').forEach((bar, index) => bar.classList.toggle('is-on', index < answered));
        steps.querySelectorAll('[data-quiz-step]').forEach((step, index) => {
          const question = data.questions[index];
          if (!question) return;
          const isDone = state.answers[question.key] != null;
          step.classList.toggle('is-done', isDone);
          step.classList.toggle('is-current', !!current && index === state.current);
          step.classList.toggle('is-next', !isDone && !(current && index === state.current));
          const chip = step.querySelector('[data-quiz-change]');
          if (chip) {
            chip.hidden = !isDone;
            const label = chip.querySelector('[data-quiz-chip]');
            const entry = chips.find((item) => item.key === question.key);
            if (label) label.textContent = entry ? entry.label : '';
          }
        });
      });
      // result screen
      if (refs.result) refs.result.hidden = !done;
      refs.actions.forEach((element) => { element.hidden = !done; });
      if (done) this.renderResult();

      // email
      if (refs.email) {
        const output = state.result;
        // Theme editor: a selected Email block previews the step even before any result exists.
        const showEmail = done && data.email && (!!output?.result || this.emailPreview === true) && state.email !== 'skipped';
        refs.email.hidden = !showEmail;
        this.placeEmail();
        if (showEmail) {
          if (refs.emailSuccess) refs.emailSuccess.hidden = state.email !== 'sent';
          if (refs.emailForm) refs.emailForm.hidden = state.email === 'sent';
          if (refs.emailSkip) refs.emailSkip.hidden = state.email === 'sent';
        }
      }

      this.saveDesignState();
    }

    renderResult() {
      const { refs, state, data } = this;
      const output = state.result || this.resolve({ track: false });
      const result = output.result;
      const designMode = !!window.Shopify?.designMode;

      if (refs.eyebrow) refs.eyebrow.textContent = data.strings.resultEyebrow || '';
      if (refs.title) refs.title.textContent = result ? quizKit.fill(result.title, output.vars, designMode) : this.strings.noResultTitle;
      if (refs.text) refs.text.innerHTML = result ? quizKit.fill(result.text || '', output.vars, designMode, escapeHtml) : '';
      if (refs.preview) {
        refs.preview.hidden = !state.preview;
        if (state.preview) refs.preview.textContent = format(this.strings.previewResult, { n: data.results.findIndex((entry) => entry.id === state.preview) + 1, total: data.results.length });
      }
      if (refs.noResult) refs.noResult.hidden = !!result;
      if (refs.cards) refs.cards.hidden = !result || output.items.length === 0;
      // Editor only: a result without products shows placeholder cards instead of an empty grid.
      const showPlaceholders = designMode && !!result && output.items.length === 0;
      if (refs.placeholders) refs.placeholders.hidden = !showPlaceholders;
      if (refs.placeholderNote) refs.placeholderNote.hidden = !showPlaceholders;

      const available = output.items.filter((item) => item.available);
      const total = available.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const hasItems = !!result && output.items.length > 0;
      const soldOut = hasItems && available.length === 0;
      const mixed = hasItems && !soldOut && available.length < output.items.length;
      const label = !hasItems ? '' : soldOut ? theme.variantStrings.soldOut : mixed ? format(this.strings.addAvailable, { count: available.length }) : output.addAllLabel;
      // Nothing to add → a real disabled button, like the theme's product form (styled by .button[disabled]).
      refs.addButtons.forEach((button) => {
        button.hidden = !hasItems;
        button.disabled = soldOut;
      });
      // shop.money_format may contain HTML (<span class="money">) → innerHTML, label escaped. Sold out shows no total.
      refs.addLabels.forEach((element) => { element.innerHTML = !hasItems ? '' : soldOut ? escapeHtml(label) : `${escapeHtml(label)} · ${money(total)}`; });
    }

    showCards() {
      const output = this.state.result;
      if (!output || !this.refs.cards) return;
      if (this.data.cards === 'fetch') {
        this.fetchCards(output);
        return;
      }
      this.applyItems(output);
    }

    applyItems(output) {
      const { cards } = this.refs;
      const items = Array.from(cards.querySelectorAll('[data-quiz-item]'));
      const byId = new Map(items.map((element) => [String(element.dataset.quizItem), element]));
      items.forEach((element) => { element.hidden = true; element.style.removeProperty('order'); });
      cards.querySelectorAll('[data-quiz-skeleton]').forEach((skeleton) => { skeleton.hidden = true; });
      cards.removeAttribute('aria-busy');

      output.items.forEach((item, index) => {
        const element = byId.get(String(item.productId));
        if (!element) return;
        element.hidden = false;
        element.style.order = index;
        this.syncCardVariant(element, item);
      });
    }

    /**
     * Shows the pinned variant in a card: writes its option values into the card's variant picker and lets the
     * theme's product-card-info re-render price and image (it fetches the product's bundle-card view).
     * A variant the shopper picked in the card is left alone.
     */
    syncCardVariant(element, item) {
      if (item.picked) return;
      const picker = element.querySelector('variant-picker');
      const form = element.querySelector('form[is="product-bundle-form"]');
      if (!picker || !form || !item.variantId || !Array.isArray(item.options) || !item.options.length) return;
      const current = form.querySelector('[name="id"]')?.value;
      if (String(current) === String(item.variantId)) return;
      let last = null;
      (picker.optionContainers || []).forEach((container, index) => {
        const value = item.options[index];
        if (value == null) return;
        if (container.tagName === 'SELECT') {
          if (container.value !== value) { container.value = value; last = container; }
        }
        else {
          const input = container.querySelector(`input[value="${CSS.escape(value)}"]`);
          if (input && !input.checked) { input.checked = true; last = input; }
        }
      });
      if (!last) return;
      // Marks the variantChange this triggers as an echo of the pin, not a shopper's pick (onCardVariantChange).
      element.dataset.quizSync = String(item.variantId);
      last.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * theme.pubsub variantChange from a result card (product-card-info): the card's current variant becomes the
     * kit item's variant — "Add all" total, availability and the cart payload follow the shopper's choice.
     * The echo of syncCardVariant (data-quiz-sync) updates the item too but does not count as a pick, so a later
     * result re-render may still move the card to a new pin. The result copy keeps the quiz's own `{variant}`.
     */
    onCardVariantChange({ data } = {}) {
      const { productId, sectionId, variant } = data || {};
      const output = this.state?.result;
      if (!output || !variant || String(sectionId) !== String(this.dataset.sectionId)) return;
      const item = output.items.find((entry) => String(entry.productId) === String(productId));
      if (!item) return;
      const element = Array.from(this.refs.cards?.querySelectorAll('[data-quiz-item]') ?? [])
        .find((node) => String(node.dataset.quizItem) === String(productId));
      const echo = !!element && element.dataset.quizSync === String(variant.id);
      if (element) delete element.dataset.quizSync;
      const changed = String(item.variantId) !== String(variant.id);
      item.variantId = variant.id;
      item.price = variant.price;
      item.available = variant.available !== false;
      if (Array.isArray(variant.options)) item.options = variant.options;
      if (!echo) item.picked = true;
      if (changed) this.renderResult();
    }

    fetchCards(output) {
      const { cards } = this.refs;
      const present = new Set(Array.from(cards.querySelectorAll('[data-quiz-item]')).map((element) => String(element.dataset.quizItem)));
      const missing = output.items.map((item) => item.productId).filter((id) => !present.has(String(id)));
      this.cardsAbort?.abort();
      if (!missing.length) { this.applyItems(output); return; }

      cards.setAttribute('aria-busy', 'true');
      cards.querySelectorAll('[data-quiz-item]').forEach((element) => { element.hidden = true; });
      cards.querySelectorAll('[data-quiz-skeleton]').forEach((skeleton) => { skeleton.hidden = false; });

      const controller = new AbortController();
      this.cardsAbort = controller;
      const url = `${this.dataset.cardsUrl}${encodeURIComponent(missing.map((id) => `id:${id}`).join(' OR '))}`;
      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`quiz-kit: cards request failed (${response.status})`);
          return response.text();
        })
        .then((html) => {
          const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quiz-cards]');
          if (!fragment) throw new Error('quiz-kit: cards container missing');
          // Search-rendered product.url carries Shopify's search tracking (?_pos=…&_ss=r); product-card-info appends
          // its own query to that URL, so the card gets the bare path (the theme's quick-view strips it the same way).
          fragment.querySelectorAll('product-card-info[data-product-url]').forEach((info) => {
            info.setAttribute('data-product-url', info.getAttribute('data-product-url').split('?')[0]);
          });
          // Fetched cards are kept (hidden) for later results; only ids not yet in the DOM are requested.
          cards.append(...Array.from(fragment.children));
          const now = new Set(Array.from(cards.querySelectorAll('[data-quiz-item]')).map((element) => String(element.dataset.quizItem)));
          const current = this.state.result || output;
          current.items.forEach((item) => {
            if (!now.has(String(item.productId))) { item.available = false; item.missing = true; }
          });
          this.renderResult();
          this.applyItems(current);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.showError(this.strings.cartError);
        })
        .finally(() => {
          if (controller !== this.cardsAbort) return;
          cards.removeAttribute('aria-busy');
          cards.querySelectorAll('[data-quiz-skeleton]').forEach((skeleton) => { skeleton.hidden = true; });
        });
    }

    /* ---------- cart ---------- */

    async addAll(button) {
      const output = this.state.result;
      if (!output || this.state.busy || button.disabled) return;
      const available = output.items.filter((item) => item.available);
      if (!available.length) return;
      const items = available.map((item) => ({ id: item.variantId, quantity: item.quantity }));

      if (!window.Shopify?.designMode && (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page')) {
        theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items } });
        return;
      }

      this.state.busy = true;
      this.showError(false);
      button.setAttribute('aria-busy', 'true');

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const body = JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname });

      try {
        const parsedState = await (await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })).json();
        if (parsedState.status) {
          this.showError(parsedState.description || parsedState.message || this.strings.cartError);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
          const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'quiz-kit', cart: cartJson });
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'quiz-kit', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

        const message = available.length === output.items.length ? this.strings.added : format(this.strings.addedOf, { count: available.length, total: output.items.length });
        this.setStatus(message);
        this.track('added', { items });

        document.querySelector('cart-drawer')?.show(button);
      }
      catch (error) {
        console.error(error);
        this.showError(this.strings.cartError);
      }
      finally {
        this.state.busy = false;
        button.removeAttribute('aria-busy');
      }
    }

    /* ---------- email ---------- */

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-quiz-email-form]')) {
        const output = this.state.result;
        if (this.refs.emailTags && output?.result) {
          // Hidden inputs keep value == defaultValue, so the merchant's base tags are captured once at init.
          const tags = new Set(quizKit.list(this.emailBaseTags ?? this.refs.emailTags.value));
          if (this.refs.emailNews?.checked) tags.add('newsletter');
          // Readable tags (merchants see them in Admin): the result heading as shown, or the result's own tag.
          const clean = (value) => String(value || '').replace(/[,\n]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 255);
          const heading = this.refs.title?.textContent || quizKit.fill(output.result.title, output.vars, false);
          tags.add(clean(output.result.tag) || `Quiz result: ${clean(heading)}`);
          this.refs.emailTags.value = Array.from(tags).join(', ');
        }
        this.persist('email');
        this.track('email');
      }
    }

    /** One DOM copy of the email block: in the panel slot on desktop, back in the stage below the actions otherwise. */
    placeEmail() {
      const { email, emailSlot } = this.refs;
      if (!email || !emailSlot) return;
      const inPanel = !!this.desktopQuery?.matches;
      if (inPanel && email.parentNode !== emailSlot) emailSlot.append(email);
      else if (!inPanel && email.parentNode === emailSlot && this.emailHome) this.emailHome.parent.insertBefore(email, this.emailHome.next);
      emailSlot.hidden = !inPanel || email.hidden;
    }

    skipEmail() {
      const hadFocus = this.contains(document.activeElement);
      this.state.email = 'skipped';
      this.render();
      if (hadFocus) this.refs.title?.focus({ preventScroll: true });
    }

    hasPendingRestore() {
      if (!theme.config.hasSessionStorage) return false;
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
        if (!stored || stored.reason !== 'email') return false;
        return window.location.search.includes('customer_posted=true') || !!this.querySelector('[data-quiz-email] .alert--error');
      }
      catch (error) {
        return false;
      }
    }

    persist(reason) {
      if (!theme.config.hasSessionStorage) return;
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({ reason, answers: this.state.answers, preview: this.state.preview }));
      }
      catch (error) {
        // Storage blocked: the alert still renders after the reload, only the result screen is lost.
      }
    }

    clearStorage() {
      if (!theme.config.hasSessionStorage) return;
      try { window.sessionStorage.removeItem(this.storageKey); } catch (error) { /* noop */ }
    }

    restore() {
      if (!this.hasPendingRestore()) return;
      let stored = null;
      try { stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null'); } catch (error) { stored = null; }
      this.clearStorage();
      if (!stored?.answers) return;
      const errored = !!this.querySelector('[data-quiz-email] .alert--error');
      this.restoring = true;
      this.state.answers = stored.answers;
      this.state.screen = 'run';
      this.state.current = this.data.questions.length;
      this.state.email = errored ? null : 'sent';
      this.resolve({ track: false });
      const focusAlert = () => {
        const alert = errored ? this.querySelector('[data-quiz-email] .alert--error') : this.refs.emailSuccess;
        if (alert && !alert.hidden) alert.focus({ preventScroll: true });
        this.restoring = false;
      };
      this.showCards();
      // Fresh page load: sections above may still be collapsed by content-visibility — measure real geometry,
      // jump (no smooth scroll across the whole page) and bring the alert into view. rAF does not fire in a
      // background tab, so a timer runs the same step once if the frame never comes.
      let aligned = false;
      const align = () => {
        if (aligned) return;
        aligned = true;
        this.settleLayout();
        this.reveal({ instant: true, target: errored ? this.querySelector('[data-quiz-email] .alert--error') : this.refs.emailSuccess });
        focusAlert();
        this.realignOnLoad(errored);
      };
      requestAnimationFrame(align);
      setTimeout(align, 300);
    }

    /**
     * While `body[data-page-rendering]` is set (until the first interaction, theme.js), theme.css gives every
     * section from the third on `content-visibility: auto` with no intrinsic size: an unrendered section above
     * the quiz is 0px tall, so the quiz measures far too high up the page. Dropping the flag here puts the page
     * in the state it enters on the first interaction anyway; the layout flush makes the geometry real before
     * measuring (theme.js's own listener removes the attribute again later, a no-op).
     */
    settleLayout() {
      if (document.body.hasAttribute('data-page-rendering')) document.body.removeAttribute('data-page-rendering');
      (this.closest('.shopify-section') ?? this).getBoundingClientRect();
    }

    /**
     * Fonts and late images can still move the page after the first alignment: once the document has loaded,
     * align again unless the shopper has started scrolling or interacting.
     */
    realignOnLoad(errored = false) {
      if (this.realigned) return;
      this.realigned = true;
      let touched = false;
      const mark = () => { touched = true; };
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((type) => this.on(window, type, mark, { once: true, passive: true }));
      const realign = () => {
        setTimeout(() => {
          if (touched || !this.isConnected || this.state.screen !== 'run') return;
          this.settleLayout();
          this.reveal({ instant: true, target: errored ? this.querySelector('[data-quiz-email] .alert--error') : this.refs.emailSuccess });
        }, 300);
      };
      if (document.readyState === 'complete') realign();
      else this.on(window, 'load', realign, { once: true });
    }

    /* ---------- design mode ---------- */

    restoreDesignState() {
      if (!window.Shopify?.designMode) return null;
      const store = window.__quizKitState = window.__quizKitState || {};
      const saved = store[this.dataset.sectionId];
      return saved ? { ...saved, result: null, busy: false } : null;
    }

    saveDesignState() {
      if (!window.Shopify?.designMode) return;
      const store = window.__quizKitState = window.__quizKitState || {};
      store[this.dataset.sectionId] = { screen: this.state.screen, current: this.state.current, answers: { ...this.state.answers }, email: this.state.email, preview: this.state.preview };
    }

    onBlockSelect(event) {
      const target = event.target;
      if (!this.initialized) this.init();
      const quiet = !!event.detail?.load;
      const fieldset = target.closest?.('[data-quiz-q]');
      if (fieldset) {
        const index = this.refs.fieldsets.indexOf(fieldset);
        if (index !== -1) { this.state.preview = null; this.state.screen = 'run'; this.go(index, { quiet, track: false }); }
        return;
      }
      const resultAnchor = target.closest?.('[data-quiz-result-block]');
      if (resultAnchor) {
        this.state.preview = resultAnchor.dataset.quizResultBlock;
        this.state.screen = 'run';
        this.state.current = this.data.questions.length;
        this.state.result = null;
        this.go(this.data.questions.length, { quiet, track: false });
        return;
      }
      if (target.closest?.('[data-quiz-email]')) {
        // Email block: jump to the result screen and show the email step, even with no answers yet.
        this.emailPreview = true;
        this.state.preview = null;
        this.state.screen = 'run';
        this.state.current = this.data.questions.length;
        this.state.email = null;
        this.state.result = null;
        this.go(this.data.questions.length, { quiet, track: false });
        return;
      }
      if (target.closest?.('.quiz-kit__fact')) {
        this.state.screen = 'intro';
        this.render();
      }
    }

    onBlockDeselect(event) {
      if (event.target.closest?.('[data-quiz-email]') && this.emailPreview) {
        this.emailPreview = false;
        this.render();
        return;
      }
      if (event.target.closest?.('[data-quiz-result-block]') && this.state.preview) {
        this.state.preview = null;
        this.state.result = null;
        if (this.done) { this.resolve({ track: false }); this.showCards(); }
        this.render();
      }
    }

    audit() {
      const { notice } = this.refs;
      if (!notice || this.audited) return;
      this.audited = true;
      const templates = this.strings.notices || {};
      const messages = quizKit.audit(this.data).map((entry) => {
        const template = templates[entry.code] || entry.code;
        return format(template, { n: entry.n ?? '', key: entry.key ?? '', token: entry.token ?? '', count: entry.count ?? '' });
      });
      const text = notice.querySelector('[data-quiz-notice-text]') || notice;
      text.textContent = messages.join(' ');
      notice.hidden = messages.length === 0;
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target;
      const action = target.closest('[data-quiz-start], [data-quiz-back], [data-quiz-next], [data-quiz-change], [data-quiz-restart], [data-quiz-add], [data-quiz-email-skip]');
      if (!action) return;
      event.preventDefault();
      if (action.hasAttribute('data-quiz-start')) return this.start();
      if (action.hasAttribute('data-quiz-back')) return this.back();
      if (action.hasAttribute('data-quiz-next')) return this.next();
      if (action.hasAttribute('data-quiz-change')) return this.change(action.dataset.quizChange);
      if (action.hasAttribute('data-quiz-restart')) return this.restart();
      if (action.hasAttribute('data-quiz-add')) return this.addAll(action);
      if (action.hasAttribute('data-quiz-email-skip')) return this.skipEmail();
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-quiz-answer]') || !input.checked) return;
      const fieldset = input.closest('[data-quiz-q]');
      if (!fieldset) return;
      this.manualNext = false;
      this.pick(fieldset.dataset.quizQ, input.value, { viaArrow: this.arrowPick === true });
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        this.arrowPick = target.matches('[data-quiz-answer]');
        return;
      }
    }

    /* ---------- helpers ---------- */

    focusCurrent() {
      const inside = this.contains(document.activeElement);
      if (!inside) return;
      const target = this.done ? this.refs.title : this.refs.fieldsets[this.state.current]?.querySelector('[data-quiz-focus]');
      target?.focus({ preventScroll: true });
    }

    /**
     * Scrolls the current screen under the sticky header when it is off-screen. `instant` skips the smooth
     * scroll (post-redirect restore); `target` (e.g. the email alert, which sits under the result cards on
     * mobile) is centred afterwards when it would still be outside the viewport.
     */
    reveal({ instant = false, target = null } = {}) {
      const behavior = instant || theme.config.motionReduced ? 'auto' : 'smooth';
      const run = this.state.screen === 'run' ? this.refs.run : this.refs.intro;
      if (!run) return;
      const offset = stickyHeaderHeight() + 16;
      const top = run.getBoundingClientRect().top;
      if (top < offset || top > window.innerHeight * 0.6) {
        window.scrollTo({ top: window.scrollY + top - offset, behavior });
      }
      if (!target || target.hidden || behavior !== 'auto') return;
      const rect = target.getBoundingClientRect();
      if (rect.top < offset || rect.bottom > window.innerHeight) {
        window.scrollTo({ top: window.scrollY + rect.top - Math.max(offset, (window.innerHeight - rect.height) / 2), behavior });
      }
    }

    announce() {
      const { live } = this.refs;
      if (!live) return;
      let message = '';
      if (this.done) {
        const output = this.state.result;
        message = output?.result ? `${this.data.strings.resultEyebrow || ''} ${quizKit.fill(output.result.title, output.vars, false)}`.trim() : this.strings.noResultTitle;
      }
      else {
        const question = this.data.questions[this.state.current];
        if (question) message = `${format(this.strings.questionOf, { n: this.state.current + 1, total: this.data.questions.length })}: ${question.title}`;
      }
      live.textContent = '';
      requestAnimationFrame(() => { live.textContent = message; });
    }

    setStatus(message) {
      const { live } = this.refs;
      if (!live) return;
      live.textContent = '';
      requestAnimationFrame(() => { live.textContent = message; });
    }

    showError(message) {
      const { error } = this.refs;
      if (!error) return;
      error.hidden = !message;
      error.textContent = message || '';
    }

    track(name, payload = {}) {
      const detail = { sectionId: this.dataset.sectionId, ...payload };
      this.dispatchEvent(new CustomEvent(`quiz:${name}`, { bubbles: true, detail }));
      try {
        window.Shopify?.analytics?.publish?.(`quiz_kit_${name}`, detail);
      }
      catch (error) {
        // Web Pixels are optional.
      }
    }
  }
  customElements.define('quiz-kit', QuizKit);
}
